// @src/pre-deployment/src/components/TrackingService/services/SilentTrackService.js v2.1
// - Migrated from src/pre-deployment/src/tracking/silentTrack.js
// - Uses RawErrorService for error logging (NOT ErrorLoggingService)
// - Maintains zero-crash policy: never throws, returns null on failure

import { writeRawError } from '../../ErrorHandling/AutomatedErrorSystem/RawErrorService';

// Conditionally import pushLog - only available in browser (React component)
let pushLog = () => { }; // Default no-op for Node.js
if (typeof window !== 'undefined') {
    import('../../shared/ConsoleOverlay.js').then(module => {
        pushLog = module.pushLog;
    }).catch(() => { });
}

/**
 * Enhanced silent tracking wrapper with RawErrorService integration
 * Wraps async operations to catch errors silently and log them properly.
 * Implements zero-crash policy: never throws exceptions, returns null on failure.
 * 
 * @template T
 * @param {() => Promise<T>} fn - Async function to execute
 * @param {string} context - Operation context for logging (e.g., 'mark_chapter_started')
 * @param {Object} options - Additional options for error handling
 * @param {string} [options.componentName='SilentTrackService'] - Component/service name for error logging
 * @param {string} [options.severity='medium'] - Error severity: 'low', 'medium', 'high', 'critical'
 * @param {number} [options.level] - Learning level context
 * @param {number} [options.chapter] - Chapter number context
 * @param {string} [options.firebasePath] - Firestore path being accessed
 * @returns {Promise<T|null>} Result of fn() or null on error (never throws)
 */
export const silentTrack = async (fn, context = 'tracking', options = {}) => {
    try {
        pushLog(`Tracking Start: ${context}`, 'info', 'tracking');

        const result = await fn();

        pushLog(`Tracking Success: ${context}`, 'success', 'tracking');

        return result;
    } catch (error) {
        pushLog(`Tracking Error (${context}): ${error.message}`, 'error', 'tracking');
        console.error(`Error in tracking operation (${context}):`, error);
        console.warn('Tracking failed but app continues normally');

        // Write to RawErrorService for all tracking errors
        await writeRawError(error, {
            componentName: options.componentName || 'SilentTrackService',
            operationType: `tracking_${context}`,
            severity: options.severity || 'medium',
            level: options.level,
            chapter: options.chapter
        });

        // Zero-crash policy: never throw, return null
        return null;
    }
};

const SilentTrackService = { silentTrack };
export default SilentTrackService;
